import React from 'react';
import PropTypes from 'prop-types';
import { formatCurrency } from 'utils/currency';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  FABFITFUN_SUPPORT,
  SUBSCRIPTION_TERMS,
  TERMS_OF_USE_AND_SALE,
} from 'utils/legalUrls';

import styles from './OrderTotal.module.scss';

const TAX_FINE_PRINT =
  '*Taxes (Import/Border) reflects sales taxes collected by CBSA at the time of import of your order and paid by FabFitFun on your behalf.';
const CANADA = 'CANADA';
const TAX_LABELS = {
  GREAT_BRITAIN: 'VAT (Import)',
  CANADA: 'Taxes (Import/Border)',
  UNITED_STATES: 'Tax',
};

const OrderTotal = ({
  subtotal,
  appliedPurchaseCredit,
  hasAppliedPurchaseCredit,
  shippingPrice,
  additionalShipping,
  tax,
  displayTax,
  country,
  total,
  showNote,
  showSubscriptionDisclaimer,
  taxError,
  appliedShoppingCredit,
  hasAppliedShoppingCredit,
}) => {
  const { shoppingCredit: shoppingCreditFF } = useFlags();
  return (
    <div>
      <div className={styles.subtotal}>
        <p id="subtotal">
          Subtotal
          <span>{formatCurrency(subtotal / 100)}</span>
        </p>
        {hasAppliedPurchaseCredit && (
          <p id="purchase-credit">
            Promotional Credit
            <span>{`-$${appliedPurchaseCredit}`}</span>
          </p>
        )}
        {hasAppliedShoppingCredit && shoppingCreditFF && (
          <p id="shopping-credit">
            Shopping Credit
            <span>{`-$${appliedShoppingCredit}`}</span>
          </p>
        )}
        <p id="shipping">
          Shipping
          <span>{`${
            shippingPrice > 0 ? formatCurrency(shippingPrice / 100) : 'Free'
          }`}</span>
        </p>
        {additionalShipping > 0 && (
          <p id="additional-shipping">
            Add’l Shipping & Handling
            <span>{formatCurrency(additionalShipping / 100)}</span>
          </p>
        )}
        {displayTax && !taxError && (
          <p id="tax">
            {TAX_LABELS[country]}
            <span>{formatCurrency(tax)}</span>
          </p>
        )}
      </div>
      <div className={styles.total}>
        <p className={styles.orderTotal}>
          <span>{taxError ? 'Total (Excluding Tax)' : 'Order Total'}</span>
          {formatCurrency(total / 100)}
        </p>
        {country === CANADA && (
          <div id="tax-fine-print" className={styles.finePrint}>
            <p>{TAX_FINE_PRINT}</p>
          </div>
        )}
        {showNote && (
          <>
            <div className={styles.separator} />
            <div id="note" className={styles.note}>
              {!displayTax && (
                <p>Prices do not include applicable sales taxes.</p>
              )}
              {showSubscriptionDisclaimer && (
                <p className={styles.subscriptionDisclaimer}>
                  Your subscriptions are ongoing. You will be automatically
                  charged the subscription price plus taxes and shipping every
                  90 days to the payment method provided on your account.
                  Foreign exchange or transaction fees may also apply. You may
                  cancel your subscription at any time before your next bill
                  date online or by contacting FabFitFun Support at
                  1.855.313.6267. For more details click{' '}
                  <a href={FABFITFUN_SUPPORT}>here</a>. By placing your order,
                  you are agreeing to our{' '}
                  <a href={TERMS_OF_USE_AND_SALE}>Terms of Use and Sale</a> and{' '}
                  <a href={SUBSCRIPTION_TERMS}>
                    Subscriptions Terms and Conditions
                  </a>
                  .
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
OrderTotal.propTypes = {
  subtotal: PropTypes.number.isRequired,
  appliedPurchaseCredit: PropTypes.string.isRequired,
  hasAppliedPurchaseCredit: PropTypes.bool.isRequired,
  shippingPrice: PropTypes.number.isRequired,
  additionalShipping: PropTypes.number.isRequired,
  tax: PropTypes.number.isRequired,
  displayTax: PropTypes.bool.isRequired,
  country: PropTypes.string.isRequired,
  total: PropTypes.number.isRequired,
  showNote: PropTypes.bool,
  showSubscriptionDisclaimer: PropTypes.bool,
  taxError: PropTypes.bool.isRequired,
  appliedShoppingCredit: PropTypes.number.isRequired,
  hasAppliedShoppingCredit: PropTypes.number.isRequired,
};

OrderTotal.defaultProps = {
  showNote: false,
  showSubscriptionDisclaimer: false,
};

export default OrderTotal;
